
import getProp from '@f/get-prop'
import { createSelector } from 'reselect'
import { getWarehouseBillingPlan } from './billing'

/**
 * Check if warehouses are available.
 *
 * @param {Object} state
 * @param {String} workspaceSlug
 * @param {Boolean} shouldRestrictFeatures
 * @return {Boolean}
 */

export const areWarehousesAvailable = createSelector(
  [getWarehouseSettingsCount, getWarehouseBillingPlan],
  (warehouseCount, plan) => {
    // always available if there are less than 1
    if (warehouseCount < 1) {
      return true
    }

    // if we don't know the plan, assume warehouses are available
    if (!plan) return true
    const planId = plan.id

    switch (planId) {
      case 'business': // Business
      case 'warehouses-business-$0': // Legacy: Business (Warehouses)
      case 'warehouses-standard-$2149-1-year': // Legacy: Standard (Warehouses, yearly)
      case 'warehouses-standard-$199-1-month': // Legacy: Standard (Warehouses, monthly)
      case 'warehouses-free-$0': // Legacy: Free (Warehouses)
      case 'integrations-business-$0': // Legacy: Business (Integrations)
        return true
      case 'team-$100-1-month': // Team
      case 'developer': // Developer
      case 'integrations-developer-$0': // Legacy: Developer (Integrations)
      case 'integrations-growth-$4849-1-year': // Legacy: Growth (Integrations, yearly)
      case 'integrations-growth-$449-1-month': // Legacy: Growth (Integrations, monthly)
      case 'integrations-project-$421-1-year': // Legacy: Project (Integrations, yearly)
      case 'integrations-project-$39-1-month': // Legacy: Project (Integrations, monthly)
      case 'integrations-startup-$1069-1-year': // Legacy: Startup (Integrations, yearly)
      case 'integrations-startup-$99-1-month': // Legacy: Startup (Integrations, monthly)
        return warehouseCount < 1
      default: // if we don't know the plan, default to 1
        return warehouseCount < 1
    }
  }
)

// This selector allows a second parameter which would typically lead to caching
// issues with `reselect`, preventing data from being memoized (reselect only
// caches the previous result). However, in practical terms, `workspace` almost
// never changes in our application
const getWarehouseSettingsState = (state, workspaceSlug) => getProp(`warehousesById.${workspaceSlug}`, state) || {}

/*
 * Derived state
 * The following should use reselect
 */

export const getWarehouseSettingsRequest = createSelector(
  getWarehouseSettingsState,
  ({ isRequesting, didRequestFail, hasRequested }) => ({ isRequesting, didRequestFail, hasRequested })
)

/**
 * Get all warehouse settings.
 *
 * @param {Object} state
 * @param {String} workspaceSlug
 * @return {Array}
 */

export const getWarehouseSettings = createSelector(
  getWarehouseSettingsState,
  (state) => {
    const warehousesById = state.data || {}
    const arr = []
    for (const id in warehousesById) {
      arr.push(warehousesById[id].data)
    }
    return arr
  }
)

export const getEnabledWarehouseSettings = (state, workspaceSlug) => {
  const warehouses = getWarehouseSettings(state, workspaceSlug)
  return warehouses.filter(w => w.enabled)
}

export function getWarehouseSettingsCount (state, workspaceSlug) {
  return getWarehouseSettings(state, workspaceSlug).length
}

export function isWorkspaceUsingWarehouseFilters (state, workspaceSlug) {
  const warehouses = getWarehouseSettings(state, workspaceSlug)
  for (let i = 0; i < warehouses.length; i++) {
    const warehouse = warehouses[i]
    if (isWarehouseUsingFilters(warehouse)) {
      return true
    }
  }
  return false
}

function isWarehouseUsingFilters (warehouse) {
  const { projectOverrides = {} } = warehouse
  for (const projectId in projectOverrides) {
    if (!projectOverrides.hasOwnProperty(projectId)) continue
    const { disabled } = projectOverrides[projectId] || {}
    if (disabled === true) {
      return true
    }
  }
  return false
}

function getWarehouseSettingByIdData (state, workspaceSlug, warehouseId) {
  return getProp(`warehousesById.${workspaceSlug}.data.${warehouseId}`, state)
}

export function getWarehouseSettingById (state, workspaceSlug, warehouseId) {
  const { data } = getWarehouseSettingByIdData(state, workspaceSlug, warehouseId) || {}
  return data
}

export function getWarehouseSettingByIdRequest (state, workspaceSlug, warehouseId) {
  const { isRequesting, didRequestFail } = getWarehouseSettingByIdData(state, workspaceSlug, warehouseId) || {}
  return { isRequesting, didRequestFail }
}



// WEBPACK FOOTER //
// ./selectors/warehouses.js